import React, { useState } from "react";
import axios from "axios";
import {
  useParams,
  useSearchParams,
  useNavigate,
  Route,
  Routes,
  Link,
} from "react-router-dom";
import { IconCake } from "@tabler/icons-react";
import {
  Box,
  Flex,
  ThemeIcon,
  Card,
  Text,
  Modal,
  Button,
  NumberFormatter,
} from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  ErrorView,
  PageTitle,
  Subnav,
  EntitySubtitles,
} from "@components/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import entityColor from "@util/entityColor";
import entityIcon from "@util/entityIcon";
import { Taggit } from "@components/Tag";
import {
  UserGroupForm,
  UserGroupUseList,
  UserGroupVisibility,
} from "@components/UserGroup";
import { SnapshotQueryVisualizer } from "@components/ReportRecipe";
import { UpdateButton } from "@components/shared/UserAdd/UserGroup";
import { TagPillList } from "@components/Tag/Taggit";

export default function UserGroupDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const {
    data: userGroup,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["user-group", id],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`/user-groups/${id}/`);
        return data.response[0];
      } catch (err) {
        throw err;
      }
    },
    retry: 1,
    retryDelay: 1000,
  });

  function fetchUserGroup() {
    return queryClient.invalidateQueries({ queryKey: ["user-group", id] });
  }

  if (isLoading || isFetching) return <div>Loading...</div>;
  if (error) return <ErrorView error={error} />;

  const editable =
    userGroup?.can_edit && !userGroup.campaign_effort_id ? true : false;

  return (
    <div>
      <PageTitle
        title={userGroup.name}
        subtitle={`${userGroup.description}`}
        style={{ marginBottom: 0 }}
      />
      <Box mt={8}>
        <Flex gap="xs" align="center">
          <ThemeIcon
            size="sm"
            radius="xl"
            variant="light"
            color={entityColor.user}
          >
            {entityIcon.userGroup()}
          </ThemeIcon>
          <Text size="sm">
            <NumberFormatter value={userGroup.user_count} thousandSeparator />{" "}
            users
          </Text>
          {userGroup.updateable && editable && (
            <Box>
              <UpdateButton
                item={{
                  ...userGroup,
                  user_group_id: id,
                }}
                fetchData={fetchUserGroup}
              />
            </Box>
          )}
        </Flex>
      </Box>
      <Box mt={8}>
        <Flex gap="xs" align="center">
          <ThemeIcon
            size="sm"
            radius="xl"
            variant="light"
            color={entityColor.user}
          >
            <IconCake size={16} />
          </ThemeIcon>
          <Text size="sm">
            created {new Date(userGroup.created_at).toLocaleString()}{" "}
            {userGroup.created_by && ` by ${userGroup.created_by}`}
          </Text>
        </Flex>
      </Box>
      <Box mt={8}>
        <Flex gap="xs" align="center">
          <UserGroupVisibility
            userGroup={userGroup}
            editable={editable}
            fetchData={fetchUserGroup}
          />
        </Flex>
      </Box>
      {editable ? (
        <Box mt={8}>
          <Flex gap="xs" align="center">
            <ThemeIcon
              size="sm"
              radius="xl"
              variant="light"
              color={entityColor.tag}
            >
              {entityIcon.tag()}
            </ThemeIcon>
            <Box style={{ flexGrow: 1 }}>
              <Taggit
                reqInfo={{ user_group_id: userGroup.id }}
                editable={editable}
              />
            </Box>
          </Flex>
        </Box>
      ) : (
        <>
          {userGroup.tags.length > 0 && (
            <Box mt={8}>
              <Flex gap="xs" align="center">
                <ThemeIcon
                  size="sm"
                  radius="xl"
                  variant="light"
                  color={entityColor.tag}
                >
                  {entityIcon.tag()}
                </ThemeIcon>
                <Box style={{ flexGrow: 1 }}>
                  <TagPillList tags={userGroup.tags} />
                </Box>
              </Flex>
            </Box>
          )}
        </>
      )}
      {userGroup.snapshot_query_visualizer_data && (
        <Box mt="lg">
          <Card>
            <SnapshotQueryVisualizer
              data={userGroup.snapshot_query_visualizer_data}
            />
          </Card>
        </Box>
      )}
      {editable && (
        <Card mt="lg">
          <UserGroupForm
            info={userGroup}
            id={userGroup.id}
            onSuccess={() => {
              fetchUserGroup();
            }}
          />
        </Card>
      )}
      <Card mt="lg">
        <Text fw={500} mb="xs" size="sm">
          Where's this group being used?
        </Text>
        <UserGroupUseList
          userGroupId={userGroup.id}
          useItems={userGroup.use_cases}
        />
      </Card>
      {editable && (
        <Flex justify="flex-end" mt="lg">
          <DeleteUserGroup
            id={userGroup.id}
            onSuccess={() => {
              navigate("/segments");
            }}
          />
        </Flex>
      )}
    </div>
  );
}

function DeleteUserGroup({ id, onSuccess }) {
  const [opened, setOpened] = useState(false);

  function onDelete() {
    return axios.delete(`/user-groups/${id}/`);
  }

  const mutation = useMutation({
    mutationFn: onDelete,
    onSuccess: () => {
      onSuccess();
      setOpened(false);
      toast.success("Segment deleted");
    },
    onError: (err) => {
      toast.error(err);
    },
  });

  return (
    <>
      <Modal
        opened={opened}
        onClose={() => {
          if (mutation.isPending) return;
          setOpened(false);
        }}
        title="Delete Segment"
        centered
      >
        <Text>Are you sure you want to delete this segment?</Text>
        <Button
          color="red"
          onClick={() => mutation.mutate()}
          loading={mutation.isPending}
          fullWidth
          mt="md"
        >
          Delete
        </Button>
      </Modal>
      <Button
        color="gray"
        onClick={() => setOpened(true)}
        leftSection={entityIcon.trash(16)}
        size="xs"
        variant="subtle"
      >
        Delete Segment
      </Button>
    </>
  );
}
